import { TZDate, tzOffset } from '@date-fns/tz';
import {
    add,
    addMonths,
    format as formatDateFn,
    getDate,
    getDay,
    isValid,
    parse,
    parseISO,
    setDay,
    setHours,
    setMinutes,
    startOfMinute,
    startOfWeek,
    toDate,
} from 'date-fns';

const MINUTE_IN_MILLISECONDS = 60_000;

// go-to formats should either be DATE or DATE_TIME_ZONED
export enum TimestampFormat {
    ISO_DATE = 'yyyy-MM-dd',
    DATE = 'LLL d, yyyy',
    DISPLAY_DATE = 'EEE • MMM d',
    MONTH_DAY = 'LLL d',
    MONTH_DAY_LONG = 'LLLL d',
    MONTH_DAY_YEAR = "LLL d ''yy",
    MONTH_DAY_YEAR_LONG = 'LLLL d yyyy',
    MONTH_DAY_YEAR_LONG_COMMA = 'LLLL d, yyyy',
    MONTH_YEAR = 'LLL yyyy',
    MONTH_YEAR_SHORT = "LLL ''yy",
    MONTH_YEAR_LONG = 'LLLL yyyy',
    TIME = "h:mmaaaaa'm'",
    TIME_ZONED = "h:mmaaaaa'm' z",
    TIME_DATE_ZONED = "haaaaa'm' z 'on' LLL d, yyyy",
    TIME_24_HOUR = 'HH:mm',
    HOUR = "haaaaa'm'",
    DATE_TIME = "LLL d, yyyy 'at' h:mmaaaaa'm'",
    DATE_TIME_ZONED = "LLL d, yyyy 'at' h:mmaaaaa'm' z",
    MONTH_DAY_TIME_ZONED = "LLL d 'at' h:mmaaaaa'm' z",
    ZONE = 'z',
    HOUR_ZONE_MONTH_DATE = 'h a z, LLL d',
    HOUR_ZONE = 'h:mm a z',
    TIME_ZONE_MONTH_DATE = 'h:mm a z, LLL d',
}

export interface FormatOptions {
    timeZone?: string;
    // TODO: locale
}

const TIME_ZONE_NAME_LOCALES: Partial<Record<string, string>> = {
    'America/St_Johns': 'en-CA',
    'Europe/London': 'en-GB',
};

const TIME_ZONE_NAME_OVERRIDES: Partial<Record<string, string>> = {
    'America/Whitehorse': 'MST',
    'Pacific/Guam': 'ChST',
    'Pacific/Pago_Pago': 'SST',
    'Pacific/Saipan': 'ChST',
};

export const parseTimestamp = (ts: Date | string | number | null | undefined) => {
    if (!ts) {
        return null;
    }
    if (typeof ts === 'string') {
        // Handle empty strings
        if (ts.trim() === '') {
            return null;
        }
        const parsed = parseISO(ts);
        // Check if parseISO returned a valid date
        if (!isValid(parsed)) {
            return null;
        }
        return parsed;
    }
    const date = toDate(ts);
    // Check if toDate returned a valid date
    if (!isValid(date)) {
        return null;
    }
    return date;
};

export const toZonedTime = (ts: Date | string | number | null | undefined, timeZone: string) => {
    const date = parseTimestamp(ts);
    if (!date) {
        return new Date(NaN);
    }

    try {
        const offsetMinutes = tzOffset(timeZone, date);
        const utcDate = new Date(date.getTime() + offsetMinutes * MINUTE_IN_MILLISECONDS);

        return new Date(
            utcDate.getUTCFullYear(),
            utcDate.getUTCMonth(),
            utcDate.getUTCDate(),
            utcDate.getUTCHours(),
            utcDate.getUTCMinutes(),
            utcDate.getUTCSeconds(),
            utcDate.getUTCMilliseconds(),
        );
    } catch (error) {
        console.warn('Error converting date to zoned time:', error, ts, timeZone);
        return new Date(NaN);
    }
};

export const fromZonedTime = (date: Date, timeZone: string) => {
    if (!isValid(date)) {
        return new Date(NaN);
    }

    try {
        const utcDate = new Date(
            Date.UTC(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                date.getHours(),
                date.getMinutes(),
                date.getSeconds(),
                date.getMilliseconds(),
            ),
        );

        const offsetMinutes = tzOffset(timeZone, utcDate);
        let result = new Date(utcDate.getTime() - offsetMinutes * MINUTE_IN_MILLISECONDS);

        // Re-check offset on the computed date to better handle DST transitions.
        const resolvedOffsetMinutes = tzOffset(timeZone, result);
        if (resolvedOffsetMinutes !== offsetMinutes) {
            result = new Date(utcDate.getTime() - resolvedOffsetMinutes * MINUTE_IN_MILLISECONDS);
        }

        return result;
    } catch (error) {
        console.warn('Error converting zoned time to date:', error, date, timeZone);
        return new Date(NaN);
    }
};

export const formatTimestamp = (
    ts: Date | string | number | null | undefined,
    format: TimestampFormat | string,
    options?: FormatOptions,
) => {
    const date = parseTimestamp(ts);
    if (!date) {
        return '';
    }
    // parseTimestamp already validates, but double-check for safety
    if (!isValid(date)) {
        return '';
    }
    try {
        if (options?.timeZone) {
            return formatDateFn(new TZDate(date, options.timeZone), format);
        }
        return formatDateFn(date, format);
    } catch (error) {
        // Handle TZDate constructor errors (e.g., invalid timezone string) or formatting errors
        console.warn('Error formatting timestamp:', error, ts);
        return '';
    }
};

export const formatTimeWithTimeZoneAbbreviation = (
    ts: Date | string | number | null | undefined,
    timeZone: string,
) => {
    const date = parseTimestamp(ts);
    if (!date) {
        return '';
    }

    const time = formatTimestamp(date, TimestampFormat.TIME, { timeZone });
    if (!time) {
        return '';
    }

    try {
        const timeZoneName = new Intl.DateTimeFormat(TIME_ZONE_NAME_LOCALES[timeZone] ?? 'en-US', {
            timeZone,
            timeZoneName: 'short',
        })
            .formatToParts(date)
            .find((part) => part.type === 'timeZoneName')?.value;

        const abbreviation = TIME_ZONE_NAME_OVERRIDES[timeZone] ?? timeZoneName;

        return abbreviation ? `${time} ${abbreviation}` : time;
    } catch (error) {
        console.warn('Error formatting timezone abbreviation:', error, ts, timeZone);
        return time;
    }
};

export const getLocalTimeZone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const toISODate = (ts: Date | string | number | null | undefined, timeZone?: string) => {
    return formatTimestamp(ts, TimestampFormat.ISO_DATE, { timeZone }) || null;
};

export const fromDateAndTime = (date: string, time: string, timeZone: string) => {
    const [hours, minutes] = time
        .toString()
        .split(':')
        .map((v: string) => parseInt(v, 10));
    const localDate = startOfMinute(
        setMinutes(setHours(parse(date, 'yyyy-MM-dd', new Date()), hours), minutes),
    );
    // Convert the local date/time in the given timezone to UTC
    return fromZonedTime(localDate, timeZone);
};

export const isValidZonedDateAndTime = (date: string, time: string, timeZone: string) => {
    const timestamp = fromDateAndTime(date, time, timeZone);

    return (
        formatTimestamp(timestamp, TimestampFormat.ISO_DATE, { timeZone }) === date &&
        formatTimestamp(timestamp, TimestampFormat.TIME_24_HOUR, { timeZone }) === time
    );
};

export const parseISODate = (date: string) => {
    return parse(date, TimestampFormat.ISO_DATE, new Date());
};

export const formatDateWindow = (
    from: Date | string | number | null | undefined,
    to: Date | string | number | null | undefined,
) => {
    from = parseTimestamp(from);
    to = parseTimestamp(to);

    if (!from || !to) {
        return '';
    }

    if (from.getTime() === to.getTime()) {
        return formatTimestamp(from, TimestampFormat.MONTH_DAY_YEAR);
    }

    return (
        formatTimestamp(
            from,
            from.getFullYear() === to.getFullYear()
                ? TimestampFormat.MONTH_DAY
                : TimestampFormat.MONTH_DAY_YEAR,
        ) +
        ' - ' +
        formatTimestamp(to, TimestampFormat.MONTH_DAY_YEAR)
    );
};

export const formatDateTimeWindow = ({
    ts1,
    ts2,
    options,
    noDate = false,
}: {
    ts1: Date | string | number | null | undefined;
    ts2: Date | string | number | null | undefined;
    options?: FormatOptions;
    noDate?: boolean;
}) => {
    ts1 = parseTimestamp(ts1);
    ts2 = parseTimestamp(ts2);
    if (!ts1 || !ts2) {
        return '';
    }

    return `${formatTimestamp(
        ts1,
        ts1.getDay() === ts2.getDay() ? 'h:mm' : 'h:mm a',
        options,
    )} - ${formatTimestamp(
        ts2,
        noDate ? TimestampFormat.HOUR_ZONE : TimestampFormat.HOUR_ZONE_MONTH_DATE,
        options,
    )}`;
};

export const getNextDayOfWeek = ({
    dayOfWeek,
    date = new Date(),
    includeToday = true,
}: {
    dayOfWeek: number;
    date?: Date;
    includeToday?: boolean;
}): Date => {
    // dayOfWeek: 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    // Calculate the start of the week considering Monday as the first day of the week
    let result = setDay(startOfWeek(date, { weekStartsOn: 1 }), dayOfWeek, { weekStartsOn: 1 });

    if (result < date || (!includeToday && result === date)) {
        // If the day is before the current date, or today but today should not be included, jump to the next week
        result = add(result, { weeks: 1 });
    }

    return result;
};

export const getNextDayOfMonth = ({
    dayOfMonth,
    date = new Date(),
    includeToday = true,
}: {
    dayOfMonth: number;
    date?: Date;
    includeToday?: boolean;
}): Date => {
    const currentDay = getDate(date);

    if (currentDay < dayOfMonth || (includeToday && currentDay === dayOfMonth)) {
        // If today's date is less than the specific day, or it's the same day and includeToday is true, set this month's date to that day
        return new Date(date.getFullYear(), date.getMonth(), dayOfMonth);
    } else {
        // If today's date is past the specific day, or it's the same day but includeToday is false, set next month's date to that day
        return addMonths(new Date(date.getFullYear(), date.getMonth(), dayOfMonth), 1);
    }
};

export const convertToSeconds = (hours: number | null) => {
    if (!hours) {
        return hours;
    }

    return Math.max(0, hours * 3600);
};

export const convertToHours = (seconds: number | null) => {
    if (!seconds) {
        return seconds;
    }

    return Math.max(0, seconds / 3600);
};

export const getZonedDate = (ts: string | null, tz: string): number | null => {
    if (!ts) {
        return null;
    }
    return getDate(new TZDate(ts, tz));
};

export const getZonedWeekday = (ts: string | null, tz: string): number | null => {
    if (!ts) {
        return null;
    }
    return getDay(new TZDate(ts, tz));
};

export const getZonedIsoDate = (ts: string | null, tz: string): string | null => {
    if (!ts) {
        return null;
    }
    return toISODate(new TZDate(ts, tz));
};
